import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { formatINR, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, Trash2, FolderKanban, IndianRupee, Plus } from "lucide-react";

const PAYMENT_STATUSES = ["Pending", "Processed", "On Hold", "Partially Paid"];

export default function VendorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canManage = ["SuperAdmin", "TeamLeader"].includes(user.role);
  const [v, setV] = useState(null);
  const [payment, setPayment] = useState({ project_id: "", total: 0, paid: 0, on_hold: 0, status: "Pending", remarks: "" });
  const [attachment, setAttachment] = useState({ name: "", url: "", project_id: "" });

  // Live balance preview
  const previewBalance = Math.max(0, Number(payment.total || 0) - Number(payment.paid || 0) - Number(payment.on_hold || 0));

  const load = async () => {
    try { const { data } = await api.get(`/vendors/${id}`); setV(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, [id]);

  const savePayment = async (e) => {
    e.preventDefault();
    if (!payment.project_id) return toast.error("Pick a linked project first");
    try {
      await api.post(`/vendors/${id}/payments`, {
        ...payment,
        total: Number(payment.total), paid: Number(payment.paid), on_hold: Number(payment.on_hold),
      });
      setPayment({ project_id: "", total: 0, paid: 0, on_hold: 0, status: "Pending", remarks: "" });
      load(); toast.success("Payment saved");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addAttachment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/vendors/${id}/attachments`, attachment);
      setAttachment({ name: "", url: "", project_id: "" });
      load(); toast.success("Attachment added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeAttachment = async (aid) => {
    try { await api.delete(`/vendors/${id}/attachments/${aid}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!v) return <div className="p-8 text-neutral-500 font-mono-tight text-sm">Loading…</div>;

  return (
    <div>
      <PageHeader title={v.company_name} description={<><Link to="/vendors" className="underline underline-offset-4">← Vendors</Link> · {v.vendor_name || "No vendor name"}</>} />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-black/10 rounded-2xl p-5 space-y-2">
          <div className="label-caps text-royal-800">Primary details</div>
          <div className="text-sm"><span className="text-neutral-500">Contact: </span>{v.contact_name || "—"}</div>
          <div className="text-sm"><span className="text-neutral-500">Email: </span>{v.email || "—"}</div>
          <div className="text-sm"><span className="text-neutral-500">Phone: </span>{v.phone || "—"}</div>
          <div className="text-sm"><span className="text-neutral-500">Projects linked: </span><b data-testid="vendor-projects-linked-count">{v.projects_linked_count}</b></div>
          {v.notes && <div className="text-xs text-ink/70 mt-2 whitespace-pre-wrap">{v.notes}</div>}
        </div>

        <div className="bg-white border border-black/10 rounded-2xl p-5 lg:col-span-2">
          <div className="label-caps text-royal-800">Services</div>
          <table className="w-full text-sm mt-2" data-testid="vendor-services-table">
            <thead><tr className="text-neutral-500 font-mono-tight text-xs uppercase"><th className="text-left py-1">Name</th><th>Charge</th><th>Timeline</th><th>Remarks</th></tr></thead>
            <tbody>
              {(v.services || []).map((s, i) => (
                <tr key={i} className="border-t border-black/5"><td className="py-1">{s.name}</td><td className="font-mono-tight">{formatINR(s.charge || 0)}</td><td className="font-mono-tight text-xs">{s.timeline || "—"}</td><td className="text-xs">{s.remarks || "—"}</td></tr>
              ))}
              {(v.services || []).length === 0 && <tr><td colSpan={4} className="py-3 text-neutral-500 text-center">No services yet — edit vendor to add.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-black/10 rounded-2xl p-5 lg:col-span-3" data-testid="vendor-projects-linked">
          <div className="label-caps text-royal-800 flex items-center gap-2"><FolderKanban size={12} /> Projects linked</div>
          <table className="w-full text-sm mt-2">
            <thead><tr className="text-neutral-500 font-mono-tight text-xs uppercase"><th className="text-left py-1">Project ID</th><th className="text-left">Project name</th><th className="text-left">Client</th></tr></thead>
            <tbody>
              {(v.projects_linked || []).map((p) => (
                <tr key={p.id} className="border-t border-black/5" data-testid={`vendor-linked-${p.id}`}>
                  <td className="py-1 font-mono-tight text-xs"><Link to={`/projects/${p.id}`} className="underline underline-offset-4">{p.id.slice(0, 8)}</Link></td>
                  <td><Link to={`/projects/${p.id}`} className="underline underline-offset-4">{p.name}</Link></td>
                  <td className="text-sm"><Link to={`/projects/${p.id}`} className="underline underline-offset-4">{p.client_name}</Link></td>
                </tr>
              ))}
              {(v.projects_linked || []).length === 0 && <tr><td colSpan={3} className="py-3 text-neutral-500 text-center">No projects linked — set Project Type on a project.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-black/10 rounded-2xl p-5 lg:col-span-3" data-testid="vendor-payments">
          <div className="label-caps text-royal-800 flex items-center gap-2"><IndianRupee size={12} /> Payments</div>
          <table className="w-full text-sm mt-2">
            <thead><tr className="text-neutral-500 font-mono-tight text-xs uppercase">
              <th className="text-left py-1">Project</th><th>Total</th><th>Paid</th><th>On Hold</th><th>Balance</th><th>Status</th><th className="text-left">Remarks</th>
            </tr></thead>
            <tbody>
              {(v.payments || []).map((p, i) => {
                const proj = (v.projects_linked || []).find((x) => x.id === p.project_id);
                return (
                  <tr key={p.id || i} className="border-t border-black/5" data-testid={`vendor-pay-row-${p.project_id}`}>
                    <td className="py-1"><Link to={`/projects/${p.project_id}`} className="underline underline-offset-4 text-xs">{proj?.name || p.project_id.slice(0, 8)}</Link></td>
                    <td className="font-mono-tight">{formatINR(p.total || 0)}</td>
                    <td className="font-mono-tight">{formatINR(p.paid || 0)}</td>
                    <td className="font-mono-tight">{formatINR(p.on_hold || 0)}</td>
                    <td className="font-mono-tight">{formatINR(p.balance ?? Math.max(0, (p.total || 0) - (p.paid || 0) - (p.on_hold || 0)))}</td>
                    <td><span className="text-xs font-mono-tight uppercase px-2 py-0.5 rounded bg-neutral-100">{p.status}</span></td>
                    <td className="text-xs text-ink/70">{p.remarks || "—"}</td>
                  </tr>
                );
              })}
              {(v.payments || []).length === 0 && <tr><td colSpan={7} className="py-3 text-neutral-500 text-center">No vendor payments yet.</td></tr>}
            </tbody>
          </table>
          {canManage && (
            <form onSubmit={savePayment} className="grid grid-cols-1 md:grid-cols-7 gap-2 mt-4" data-testid="vendor-payment-form">
              <select data-testid="pay-project" required value={payment.project_id} onChange={(e) => setPayment({ ...payment, project_id: e.target.value })} className="rounded-lg border border-black/15 px-2 h-10 bg-white text-sm">
                <option value="">Pick a linked project</option>
                {(v.projects_linked || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <Input data-testid="pay-total" type="number" placeholder="Total" value={payment.total} onChange={(e) => setPayment({ ...payment, total: e.target.value })} className="rounded-lg border-black/15 h-10" />
              <Input data-testid="pay-paid" type="number" placeholder="Paid" value={payment.paid} onChange={(e) => setPayment({ ...payment, paid: e.target.value })} className="rounded-lg border-black/15 h-10" />
              <Input data-testid="pay-hold" type="number" placeholder="On Hold" value={payment.on_hold} onChange={(e) => setPayment({ ...payment, on_hold: e.target.value })} className="rounded-lg border-black/15 h-10" />
              <select data-testid="pay-status" value={payment.status} onChange={(e) => setPayment({ ...payment, status: e.target.value })} className="rounded-lg border border-black/15 px-2 h-10 bg-white text-sm">
                {PAYMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <Input data-testid="pay-remarks" placeholder="Remarks (holds, notes…)" value={payment.remarks} onChange={(e) => setPayment({ ...payment, remarks: e.target.value })} className="rounded-lg border-black/15 h-10" />
              <button data-testid="pay-save" className="btn-primary">Save</button>
            </form>
          )}
          {canManage && (
            <div className="mt-2 text-xs text-ink/70 font-mono-tight" data-testid="pay-balance-preview">
              Preview balance for this entry: <b>{formatINR(previewBalance)}</b>
            </div>
          )}
        </div>

        <div className="bg-white border border-black/10 rounded-2xl p-5 lg:col-span-3" data-testid="vendor-attachments">
          <div className="label-caps text-royal-800 flex items-center gap-2"><Paperclip size={12} /> Vendor invoices & attachments</div>
          <ul className="mt-2 space-y-1">
            {(v.attachments || []).map((a) => (
              <li key={a.id} className="flex items-center justify-between border border-black/10 rounded-lg px-3 py-2 text-sm" data-testid={`vendor-attachment-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <a href={a.url} target="_blank" rel="noreferrer" className="underline underline-offset-4">{a.name}</a>
                  <div className="text-xs text-neutral-500 font-mono-tight">by {a.uploaded_by} · {formatDate(a.uploaded_at)}
                    {a.project_id ? ` · project ${(v.projects_linked || []).find((p) => p.id === a.project_id)?.name || a.project_id.slice(0, 8)}` : ""}</div>
                </div>
                {canManage && <button data-testid={`vendor-attachment-del-${a.id}`} onClick={() => removeAttachment(a.id)} className="text-red-600 ml-3"><Trash2 size={14} /></button>}
              </li>
            ))}
            {(v.attachments || []).length === 0 && <li className="text-neutral-500 text-center py-3">No attachments yet.</li>}
          </ul>
          {canManage && (
            <form onSubmit={addAttachment} className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-3" data-testid="vendor-attachment-form">
              <Input data-testid="att-name" required placeholder="Invoice name" value={attachment.name} onChange={(e) => setAttachment({ ...attachment, name: e.target.value })} className="rounded-lg border-black/15 h-10" />
              <Input data-testid="att-url" required placeholder="https://…" value={attachment.url} onChange={(e) => setAttachment({ ...attachment, url: e.target.value })} className="rounded-lg border-black/15 h-10" />
              <select data-testid="att-project" value={attachment.project_id} onChange={(e) => setAttachment({ ...attachment, project_id: e.target.value })} className="rounded-lg border border-black/15 px-2 h-10 bg-white text-sm">
                <option value="">Not linked to a project</option>
                {(v.projects_linked || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <button data-testid="att-save" className="btn-primary inline-flex items-center gap-1"><Plus size={14} /> Attach</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
